/**
 * Mission Chat Page
 * Displays the streaming chat interface for a specific mission
 * URL: /mission/:missionId
 *
 * For BYOH missions: Loads host info for context
 * For platform missions: Direct streaming
 */

import React, { useEffect, useState, useRef, useCallback } from 'react'
import { useParams, useSearchParams, Navigate } from 'react-router'
import { useTranslation } from 'react-i18next'
import { Button } from '@agion/ui'
import { useAuth } from '@agion/auth'
import { missionViewerIdentity, useMissionStore } from '../hooks/useMissions'
import { hostsApi } from '../services/api'
import { MissionChatWithArtifacts } from '../components/chat/MissionChatWithArtifacts'
import { useChatSurface } from '../components/chat/chatSurface'

export interface HostInfo {
  id: string
  name: string
  status: string
  isConnected: boolean
}

export const MissionChatPage: React.FC = () => {
  const { t } = useTranslation(['pages', 'common'])
  const { missionId } = useParams<{ missionId: string }>()
  const [searchParams] = useSearchParams()
  const autostart = searchParams.has('autostart')
  const executionIdFromUrl = searchParams.get('execution')
  const { missions, loadMissions, loadMissionDetails: loadDetails } = useMissionStore()
  // A surface that shows run readiness asks for it (persona F-CHAT-08); the
  // current chat's read is unchanged.
  const asksRunReadiness = useChatSurface().runReadiness === true
  const loadMissionDetails = useCallback(
    (id: string) => (asksRunReadiness ? loadDetails(id, { runReadiness: true }) : loadDetails(id)),
    [asksRunReadiness, loadDetails],
  )

  const [isInitialized, setIsInitialized] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [isByohMission, setIsByohMission] = useState(false)
  const [hostInfo, setHostInfo] = useState<HostInfo | null>(null)
  const [isLoadingHost, setIsLoadingHost] = useState(false)

  const initRef = useRef(false)
  const lastMissionIdRef = useRef<string | null>(null)

  // The store withdraws the old answer synchronously; refresh it for the new viewer.
  useEffect(() => useAuth.subscribe((state, previous) => {
    if (missionId && missionViewerIdentity(state) !== missionViewerIdentity(previous)) {
      void loadMissionDetails(missionId)
    }
  }), [missionId, loadMissionDetails])

  // Reset initialization when missionId changes
  useEffect(() => {
    if (missionId !== lastMissionIdRef.current) {
      initRef.current = false
      setIsInitialized(false)
      setIsByohMission(false)
      setHostInfo(null)
      setError(null)
      lastMissionIdRef.current = missionId || null
    }
  }, [missionId])

  // Load missions data
  useEffect(() => {
    const loadData = async () => {
      if (!missionId) return

      try {
        await loadMissions()
      } catch (err) {
        console.error('[MissionChatPage] Failed to load missions:', err)
      }
    }

    loadData()
  }, [missionId, loadMissions])

  // Initialize once missions are loaded
  const missionsLoaded = missions.length > 0

  useEffect(() => {
    if (!missionId || initRef.current) return
    if (!missionsLoaded) return

    const initChat = async () => {
      try {
        // Load full mission details (agents with capabilities) — needed for voice detection
        await loadMissionDetails(missionId)

        const mission = useMissionStore.getState().getMissionById(missionId)
        if (!mission) {
          setError(t('pages:missionChat.notFound', 'Mission not found'))
          initRef.current = true
          return
        }

        const hostId = mission.hostId
        if (hostId) {
          setIsByohMission(true)
          setIsLoadingHost(true)

          try {
            const hostResponse = await hostsApi.getHost(hostId)
            setHostInfo({
              id: hostResponse.id,
              name: hostResponse.name,
              status: hostResponse.status,
              isConnected: hostResponse.vpn_config?.peer_connected ?? false,
            })
          } catch (hostErr) {
            console.error('[MissionChatPage] Failed to load host info:', hostErr)
          } finally {
            setIsLoadingHost(false)
          }
        } else {
          setIsByohMission(false)
        }

        initRef.current = true
        setIsInitialized(true)
      } catch (err) {
        console.error('[MissionChatPage] Failed to initialize:', err)
        setError(t('pages:missionChat.loadError', 'Failed to load mission'))
        initRef.current = true
      }
    }

    initChat()
  }, [missionId, missionsLoaded, t])

  const handleRetry = useCallback(() => {
    setError(null)
    setIsInitialized(false)
    initRef.current = false
  }, [])

  // If no mission ID, redirect to main chat
  if (!missionId) {
    return <Navigate to="/chat" replace />
  }

  // Streaming path — render once initialized
  if (isInitialized) {
    return (
      <MissionChatWithArtifacts
        missionId={missionId}
        autostart={autostart}
        executionId={executionIdFromUrl}
        hostName={hostInfo?.name}
      />
    )
  }

  // Show error state
  if (error) {
    const errorMessage = error
    const isConnectionError = errorMessage.includes('502') || errorMessage.includes('Failed to fetch') || errorMessage.includes('Network error')
    const isPermissionError = errorMessage.includes('403') || errorMessage.includes('Forbidden') || errorMessage.includes('permission')
    const isNotFoundError = errorMessage.includes('404') || errorMessage.includes('not found') || errorMessage.includes('Not Found')
    const isHostError = errorMessage.includes('host') || errorMessage.includes('Host')
    const isTimeoutError = errorMessage.includes('timeout') || errorMessage.includes('Timeout')

    // BYOH mission with connection error but host is connected
    if (isByohMission && hostInfo?.isConnected && isConnectionError) {
      return (
        <div className="flex items-center justify-center h-full">
          <div className="text-center max-w-md px-6">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-yellow-100 dark:bg-yellow-900/30 flex items-center justify-center">
              <div className="w-4 h-4 rounded-full bg-yellow-500" />
            </div>
            <h2 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">
              {t('pages:missionChat.agentNotResponding', 'Agent Not Responding')}
            </h2>
            <p className="text-sm text-slate-600 dark:text-slate-400 mb-4">
              {t(
                'pages:missionChat.agentNotRespondingDescription',
                'The host "{{hostName}}" is connected, but the agent runtime is not responding. Make sure the agent application is running on the host.',
                { hostName: hostInfo.name }
              )}
            </p>
            <div className="flex flex-col gap-2 items-center">
              <Button onClick={handleRetry}>
                {t('pages:missionChat.retryConnection', 'Retry Connection')}
              </Button>
              <p className="text-xs text-slate-500 dark:text-slate-500">
                {t('pages:missionChat.agentNotRespondingHint', 'Check that the agion-sdk agent is running and listening on port 8190.')}
              </p>
            </div>
          </div>
        </div>
      )
    }

    // Permission denied
    if (isPermissionError) {
      return (
        <div className="flex items-center justify-center h-full">
          <div className="text-center max-w-md px-6">
            <h2 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">
              {t('pages:missionChat.accessDenied', 'Access Denied')}
            </h2>
            <p className="text-sm text-slate-600 dark:text-slate-400 mb-4">
              {t('pages:missionChat.accessDeniedDescription', 'You don\'t have permission to access this mission.')}
            </p>
          </div>
        </div>
      )
    }

    // Connection/Network error
    if (isConnectionError || isTimeoutError) {
      return (
        <div className="flex items-center justify-center h-full">
          <div className="text-center max-w-md px-6">
            <h2 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">
              {t('pages:missionChat.connectionError', 'Connection Error')}
            </h2>
            <p className="text-sm text-slate-600 dark:text-slate-400 mb-4">
              {t('pages:missionChat.connectionErrorDescription', 'Unable to connect to the mission.')}
            </p>
            <Button onClick={handleRetry}>
              {t('pages:missionChat.retry', 'Try Again')}
            </Button>
          </div>
        </div>
      )
    }

    // Mission not found
    if (isNotFoundError && !isHostError) {
      return (
        <div className="flex items-center justify-center h-full">
          <div className="text-center max-w-md px-6">
            <h2 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">
              {t('pages:missionChat.notFound', 'Mission Not Found')}
            </h2>
            <p className="text-sm text-slate-600 dark:text-slate-400">
              {t('pages:missionChat.notFoundDescription', 'This mission doesn\'t exist or may have been deleted.')}
            </p>
          </div>
        </div>
      )
    }

    // Host-related error
    if (isHostError || (isByohMission && !hostInfo)) {
      return (
        <div className="flex items-center justify-center h-full">
          <div className="text-center max-w-md px-6">
            <h2 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">
              {t('pages:missionChat.hostNotFound', 'Host Not Available')}
            </h2>
            <p className="text-sm text-slate-600 dark:text-slate-400 mb-4">
              {t('pages:missionChat.hostNotFoundDescription', 'The agent host for this mission could not be found.')}
            </p>
            <Button onClick={handleRetry}>
              {t('pages:missionChat.retry', 'Try Again')}
            </Button>
          </div>
        </div>
      )
    }

    // Generic error fallback
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center max-w-md px-6">
          <h2 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">
            {t('pages:missionChat.error', 'Something Went Wrong')}
          </h2>
          <p className="text-xs text-slate-400 dark:text-slate-500 mb-4 font-mono bg-slate-100 dark:bg-slate-800 rounded px-3 py-2">
            {errorMessage}
          </p>
          <Button onClick={handleRetry}>
            {t('pages:missionChat.retry', 'Try Again')}
          </Button>
        </div>
      </div>
    )
  }

  // Loading state
  if (!isInitialized || isLoadingHost) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-pulse text-slate-500 dark:text-slate-400">
          {isLoadingHost
            ? t('pages:missionChat.loadingHost', 'Connecting to host...')
            : t('pages:missionChat.loading', 'Loading mission chat...')}
        </div>
      </div>
    )
  }

  // Show offline state for BYOH missions with disconnected host
  if (isByohMission && hostInfo && !hostInfo.isConnected) {
    const mission = useMissionStore.getState().getMissionById(missionId)
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center max-w-md px-6">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
            <div className="w-4 h-4 rounded-full bg-red-500" />
          </div>
          <h2 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">
            {t('pages:missionChat.hostOffline', 'Host Offline')}
          </h2>
          <p className="text-sm text-slate-600 dark:text-slate-400 mb-4">
            {t(
              'pages:missionChat.hostOfflineDescription',
              'The agent host "{{hostName}}" is currently offline.',
              { hostName: hostInfo.name, missionName: mission?.name || 'Unknown' }
            )}
          </p>
          <div className="flex flex-col gap-2 items-center">
            <Button
              onClick={() => {
                setIsLoadingHost(true)
                setIsInitialized(false)
                initRef.current = false
              }}
            >
              {t('pages:missionChat.retryConnection', 'Retry Connection')}
            </Button>
            <p className="text-xs text-slate-500">
              {t('pages:missionChat.hostOfflineHint', 'Make sure the agent host is running and connected to the VPN.')}
            </p>
          </div>
        </div>
      </div>
    )
  }

  return null
}

export default MissionChatPage
